import { MapPin, ChevronDown, ChevronUp, AlertCircle } from "lucide-react";
import { useState, useEffect, useMemo, useRef } from "react";
import { UseFormReturn } from "react-hook-form";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import { FIELD_CLASSES } from "@shared/utils/constants";
import {
  useTerritoryJson,
  useTerritoryPremiseStats,
  useUpdateAssignedServiceAddress,
  useCheckUnitWiseConsumer,
} from "@features/cx/consumerOnboarding/hooks";
import { usePatchConfigMap } from "@features/onboarding/utility-setup/hooks";
import { AttachUtilityServiceDialog } from "./attachUtilityServiceDialog";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { queryClient } from "@shared/api/queries/queryClient";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { toast } from "@shared/ui/use-toast";

export let isPremiseButtonClicked = false;

// TypeScript Interfaces
export interface LocationDetails {
  city: string;
  area: string;
  subArea: string;
  routeName: string;
  billingCycleName: string;
}

export interface PremiseOption {
  id: string;
  label: string;
  locationDetails?: LocationDetails;
}

export interface SelectServicePremiseData {
  servicePremises: string[];
  serviceUnits?: string[];
}

// API Response Interface
interface PremiseAPIResponse {
  name?: string;
  code?: string;
  hierarchyNames?: {
    state?: string;
    area?: string;
    subArea?: string;
  };
}

export interface SelectServicePremiseProps {
  data?: { result?: PremiseAPIResponse[] } | null;
  onPremiseSelect?: (
    servicePremises: string[],
    allLocationDetails?: LocationDetails[],
    serviceUnits?: string[],
  ) => void;
  initialServicePremises?: string[];
  initialServiceUnits?: string[];
  maxSelection?: number;
  isCollapsible?: boolean;
  defaultExpanded?: boolean;
  className?: string;
  consumerId?: string;
  consumerDetailsData?: any; // Add consumer details data prop
  onUnitAdded?: (isAdded: boolean) => void;
  isReadOnly?: boolean;
}

// Transform API data to PremiseOption format
const transformPremiseData = (
  apiData?: { result?: PremiseAPIResponse[] } | null,
): PremiseOption[] => {
  if (!apiData || !apiData.result || !Array.isArray(apiData.result)) {
    return [];
  }

  return apiData.result
    .filter((premise) => premise?.code && premise?.name)
    .map((premise) => ({
      id: premise.code || "",
      label: premise.name || "",
      locationDetails: {
        city: premise.hierarchyNames?.state || "N/A",
        area: premise.hierarchyNames?.area || "N/A",
        subArea: premise.hierarchyNames?.subArea || "N/A",
        routeName: "N/A",
        billingCycleName: "N/A",
      },
    }));
};

// Main Component
export const SelectServicePremise: React.FC<SelectServicePremiseProps> = ({
  data,
  onPremiseSelect,
  initialServicePremises = [],
  initialServiceUnits = [],
  maxSelection = 5,
  isCollapsible = true,
  defaultExpanded = true,
  className = "",
  consumerId,
  consumerDetailsData, // Destructure consumer details data
  onUnitAdded,
  isReadOnly = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [selectedServicePremises, setSelectedServicePremises] = useState<
    PremiseOption[]
  >([]);

  const hasAutoPopulatedPremise = useRef(false);
  const hasAutoPopulatedUnit = useRef(false);

  // Initialize the update assigned service address mutation
  const {
    mutate: updateAssignedServiceAddress,
    isSuccess,
    isPending,
  } = useUpdateAssignedServiceAddress();
  const checkUnitWiseConsumerMutation = useCheckUnitWiseConsumer();
  const patchConfigMapMutation = usePatchConfigMap();
  const [disablePassPremise, setDisablePassPremise] = useState(false);
  const [premiseAdded, setPremiseAdded] = useState(false);
  const [isNoUtilityDialogOpen, setIsNoUtilityDialogOpen] = useState(false);
  const [isAttachingUtilityServices, setIsAttachingUtilityServices] =
    useState(false);

  useEffect(() => {
    if (isSuccess) {
      setDisablePassPremise(true);
      setPremiseAdded(true);
    }
  }, [isSuccess]);

  // Extract address data from consumer details
  const serviceAddressData =
    consumerDetailsData?.result?.addressData?.service || {};
  const ownershipDetails = consumerDetailsData?.result?.ownershipDetails || {};
  console.warn(ownershipDetails, "ownershipDetails");
  // Transform API data to premise options
  const premiseOptions = transformPremiseData(data);

  const isUnitAdded = useMemo(() => {
    const hasConsumerUnit = Boolean(
      consumerDetailsData?.result?.unitInfo?.basicInfo?.code ||
      consumerDetailsData?.result?.unitInfo?.code ||
      consumerDetailsData?.result?.territoryData?.service?.unit ||
      consumerDetailsData?.result?.addressData?.service?.unit ||
      (initialServiceUnits && initialServiceUnits.length > 0),
    );
    return (
      disablePassPremise ||
      premiseAdded ||
      isReadOnly ||
      (hasConsumerUnit &&
        (isPremiseButtonClicked ||
          disablePassPremise ||
          premiseAdded ||
          isReadOnly))
    );
  }, [
    disablePassPremise,
    premiseAdded,
    isReadOnly,
    consumerDetailsData,
    initialServiceUnits,
  ]);

  useEffect(() => {
    if (onUnitAdded) {
      onUnitAdded(isUnitAdded);
    }
  }, [isUnitAdded, onUnitAdded]);

  const hasPremisesAlready = initialServicePremises.length > 0;

  const isDisabled = premiseAdded || hasPremisesAlready || isReadOnly;

  const effectiveInitialUnits = useMemo(() => {
    if (initialServiceUnits && initialServiceUnits.length > 0) {
      return initialServiceUnits;
    }
    const consumerUnit = consumerDetailsData?.result?.unitInfo?.basicInfo?.code;
    if (consumerUnit) {
      return [consumerUnit];
    }
    return [];
  }, [initialServiceUnits, consumerDetailsData?.result?.unitInfo]);

  // Track selected premise code for useTerritoryJson
  const [selectedPremiseCode, setSelectedPremiseCode] = useState<string>(
    initialServicePremises.length > 0 ? initialServicePremises[0] : "",
  );

  // Track selected unit code for useTerritoryPremiseStats
  const [selectedUnitCode, setSelectedUnitCode] = useState<string>("");

  console.log(selectedUnitCode, "selectedUnitCode");

  // Call useTerritoryJson hook with config_level="unit" and code_list=selectedPremiseCode (premise.code)
  const { data: unitTerritoryData } = useTerritoryJson(
    "unit",
    selectedPremiseCode,
  );

  // Call useTerritoryPremiseStats hook with selectedUnitCode (data.key || data.code || data.id)
  const { data: territoryPremiseStatsData } =
    useTerritoryPremiseStats(selectedUnitCode);

  const consumerUtilityServices: string[] = useMemo(() => {
    const rawServices =
      consumerDetailsData?.result?.utilityService ||
      consumerDetailsData?.result?.utilityServices ||
      consumerDetailsData?.result?.utility_service ||
      [];
    return Array.isArray(rawServices) ? rawServices : [];
  }, [consumerDetailsData]);

  const unitUtilityServices: string[] = useMemo(() => {
    const rawServices =
      territoryPremiseStatsData?.basicInfo?.utilityServices ||
      territoryPremiseStatsData?.basicInfo?.utility_services ||
      territoryPremiseStatsData?.basic_info?.utility_services ||
      territoryPremiseStatsData?.basic_info?.utilityServices ||
      [];
    return Array.isArray(rawServices) ? rawServices : [];
  }, [territoryPremiseStatsData]);

  const hasUnitUtilityServices = unitUtilityServices.length > 0;

  const [occupiedConsumerInfo, setOccupiedConsumerInfo] = useState<any>(null);

  // Helper check if occupied consumer is DIFFERENT from current consumer
  const currentConsumerNo =
    consumerDetailsData?.result?.basicInfo?.consumerNo ||
    consumerDetailsData?.result?.consumerNo ||
    consumerDetailsData?.result?.consumer_no;

  const currentConsumerId =
    consumerDetailsData?.result?.basicInfo?.id ||
    consumerDetailsData?.result?.id ||
    consumerId;

  const isOccupiedByAnotherConsumer = (occupiedRes: any) => {
    if (!occupiedRes || Object.keys(occupiedRes).length === 0) return false;

    const occupiedNo = occupiedRes.consumerNo || occupiedRes.consumer_no;
    const occupiedId =
      occupiedRes.id || occupiedRes.consumerId || occupiedRes.consumer_id;

    if (
      currentConsumerNo &&
      occupiedNo &&
      String(currentConsumerNo).trim().toUpperCase() ===
        String(occupiedNo).trim().toUpperCase()
    ) {
      return false;
    }

    if (
      currentConsumerId &&
      occupiedId &&
      String(currentConsumerId).trim().toUpperCase() ===
        String(occupiedId).trim().toUpperCase()
    ) {
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (selectedUnitCode) {
      checkUnitWiseConsumerMutation
        .mutateAsync({
          unitCode: selectedUnitCode,
          remoteUtilityId: getRemoteUtilityId(),
          isCurrent: 1,
        })
        .then((res) => {
          if (res?.result && isOccupiedByAnotherConsumer(res.result)) {
            setOccupiedConsumerInfo(res.result);
          } else {
            setOccupiedConsumerInfo(null);
          }
        })
        .catch((err) => {
          console.error("Failed to fetch unit consumer info:", err);
          setOccupiedConsumerInfo(null);
        });
    } else {
      setOccupiedConsumerInfo(null);
    }
  }, [selectedUnitCode, consumerDetailsData, consumerId]);

  const getUnitOptions = useMemo(() => {
    const rawData =
      unitTerritoryData?.result ||
      (Array.isArray(unitTerritoryData) ? unitTerritoryData : null);
    if (!rawData || !Array.isArray(rawData)) {
      return [];
    }
    return rawData.map((data: any) => ({
      value: data.code,
      label: data.name,
    }));
  }, [unitTerritoryData]);

  // Define form fields with dynamic unit options from useTerritoryJson
  const formFields: FormField[] = useMemo(
    () => [
      {
        name: "servicePremises",
        label: "Service Premises",
        type: "multi-select",
        required: true,
        placeholder: "Select service premises",
        options: premiseOptions.map((premise) => ({
          value: premise.id,
          label: premise.label,
        })),
        defaultValue: initialServicePremises,
        maxSelection: 1,
        searchable: true,
        clearable: true,
        closeOnSelect: false,
        maxDisplayItems: 2,
        classes: FIELD_CLASSES,
        disabled: isDisabled,
      },
      {
        name: "serviceUnits",
        label: "Service Units",
        type: "multi-select",
        required: true,
        placeholder: "Select service units",
        options: getUnitOptions,
        defaultValue: initialServiceUnits,
        showWhen: (formData: any) =>
          Array.isArray(formData?.servicePremises)
            ? formData.servicePremises.length > 0
            : Boolean(formData?.servicePremises),
        maxSelection: 1,
        searchable: true,
        clearable: true,
        closeOnSelect: false,
        maxDisplayItems: 2,
        classes: FIELD_CLASSES,
        disabled: isDisabled,
      },
    ],
    [
      premiseOptions,
      getUnitOptions,
      initialServicePremises,
      initialServiceUnits,
      isDisabled,
    ],
  );

  // Create form
  const form: UseFormReturn<SelectServicePremiseData> = FormService.createForm({
    fields: formFields,
    defaultValues: {
      servicePremises: initialServicePremises,
      serviceUnits: effectiveInitialUnits,
    },
  });

  // Watch for premise and unit changes
  const selectedServicePremiseIds = form.watch("servicePremises") || [];
  const selectedServiceUnitIds = form.watch("serviceUnits") || [];

  // Update selectedPremiseCode state when watched premise selection changes
  useEffect(() => {
    const currentCode =
      selectedServicePremiseIds.length > 0 ? selectedServicePremiseIds[0] : "";
    if (currentCode !== selectedPremiseCode) {
      setSelectedPremiseCode(currentCode);
    }
  }, [selectedServicePremiseIds, selectedPremiseCode]);

  // Update selectedUnitCode state ONLY from getUnitOptions
  useEffect(() => {
    const currentUnit =
      selectedServiceUnitIds.length > 0 ? selectedServiceUnitIds[0] : "";

    const matchingUnitOption = getUnitOptions.find(
      (opt: any) =>
        String(opt.value) === String(currentUnit) ||
        String(opt.label) === String(currentUnit),
    );

    const validUnitCode = matchingUnitOption ? matchingUnitOption.value : "";

    if (validUnitCode !== selectedUnitCode) {
      setSelectedUnitCode(validUnitCode);
    }
    if (validUnitCode) {
      isPremiseButtonClicked = true;
    }
  }, [selectedServiceUnitIds, getUnitOptions, selectedUnitCode]);

  // Update selected premises when dropdown changes
  useEffect(() => {
    if (selectedServicePremiseIds.length > 0) {
      const selected = premiseOptions.filter((p) =>
        selectedServicePremiseIds.includes(p.id),
      );
      setSelectedServicePremises(selected);
    } else {
      setSelectedServicePremises([]);
    }
  }, [selectedServicePremiseIds]);

  // Notify parent component of changes
  useEffect(() => {
    if (onPremiseSelect) {
      const allLocationDetails = [...selectedServicePremises]
        .map((p) => p.locationDetails)
        .filter((ld): ld is LocationDetails => ld !== undefined);

      onPremiseSelect(
        selectedServicePremiseIds,
        allLocationDetails,
        selectedUnitCode ? [selectedUnitCode] : [],
      );
    }
  }, [
    selectedServicePremiseIds,
    selectedUnitCode,
    selectedServicePremises,
    onPremiseSelect,
  ]);

  const hasAutoPopulatedInitialPremise = useRef(false);
  const hasAutoPopulatedInitialUnit = useRef(false);

  // Update form when initial values change
  useEffect(() => {
    if (
      !hasAutoPopulatedInitialPremise.current &&
      initialServicePremises.length > 0 &&
      premiseOptions.length > 0
    ) {
      form.setValue("servicePremises", initialServicePremises);
      hasAutoPopulatedInitialPremise.current = true;
    }
    if (
      !hasAutoPopulatedInitialUnit.current &&
      effectiveInitialUnits.length > 0
    ) {
      const currentUnitVal = form.getValues("serviceUnits") || [];
      if (currentUnitVal.length === 0) {
        form.setValue("serviceUnits", effectiveInitialUnits);
        isPremiseButtonClicked = true;
        hasAutoPopulatedInitialUnit.current = true;
      }
    }
  }, [initialServicePremises, effectiveInitialUnits, premiseOptions, form]);

  // Auto-patch premise from consumerDetailsData
  useEffect(() => {
    if (
      !hasAutoPopulatedPremise.current &&
      premiseOptions.length > 0 &&
      consumerDetailsData?.result?.territoryData
    ) {
      const servicePremiseLabel =
        consumerDetailsData.result.territoryData.service?.premise;

      const currentServiceVal = form.getValues("servicePremises") || [];
      if (servicePremiseLabel && currentServiceVal.length === 0) {
        const matchingPremise = premiseOptions.find(
          (p) => p.label === servicePremiseLabel,
        );
        if (matchingPremise) {
          form.setValue("servicePremises", [matchingPremise.id]);
          setPremiseAdded(true);
          setDisablePassPremise(true);
          hasAutoPopulatedPremise.current = true;
        }
      }
    }
  }, [premiseOptions, consumerDetailsData?.result?.territoryData, form]);

  // Auto-populate unit from consumerDetailsData.result.unitInfo
  useEffect(() => {
    const consumerUnit = consumerDetailsData?.result?.unitInfo?.basicInfo?.code;
    if (
      !hasAutoPopulatedUnit.current &&
      consumerUnit &&
      getUnitOptions.length > 0
    ) {
      const currentUnitVal = form.getValues("serviceUnits") || [];
      if (currentUnitVal.length === 0) {
        const matchingUnit = getUnitOptions.find(
          (opt: any) =>
            opt.value === consumerUnit ||
            opt.label === consumerUnit ||
            (typeof opt.value === "string" && opt.value.endsWith(consumerUnit)),
        );
        if (matchingUnit) {
          form.setValue("serviceUnits", [matchingUnit.value]);
          isPremiseButtonClicked = true;
          hasAutoPopulatedUnit.current = true;
        }
      }
    }
  }, [getUnitOptions, consumerDetailsData?.result?.unitInfo, form]);

  const toggleExpand = () => {
    if (isCollapsible) {
      setIsExpanded(!isExpanded);
    }
  };

  // Handle Add Premise button click
  const handleAddPremise = async () => {
    isPremiseButtonClicked = true;
    if (!consumerId) {
      return;
    }

    if (
      occupiedConsumerInfo &&
      isOccupiedByAnotherConsumer(occupiedConsumerInfo)
    ) {
      toast({
        title: "Unit already assigned",
        description: `This unit is already assigned to consumer ${
          occupiedConsumerInfo.name || occupiedConsumerInfo.consumerNo || ""
        }. Please select a different unit.`,
        variant: "destructive",
      });
      return;
    }

    // If selected unit has no utility services, open prompt dialog
    if (!hasUnitUtilityServices) {
      setIsNoUtilityDialogOpen(true);
      return;
    }

    await executeAddPremise();
  };

  // User declines attaching consumer utility services to unit
  const handleNoUtilityDecline = () => {
    setIsNoUtilityDialogOpen(false);
    toast({
      title: "Update Unit Required",
      description:
        "Please update the unit and add utility service to the unit.",
      variant: "destructive",
    });
  };

  // User confirms attaching consumer utility services to unit
  const handleNoUtilityConfirm = async () => {
    try {
      setIsAttachingUtilityServices(true);
      if (selectedUnitCode && consumerUtilityServices.length > 0) {
        const consumerPlanId =
          consumerDetailsData?.result?.basicInfo?.planId ||
          consumerDetailsData?.result?.planId ||
          consumerDetailsData?.result?.remotePlanId ||
          consumerDetailsData?.result?.remote_plan_id ||
          consumerDetailsData?.result?.unitInfo?.basicInfo?.planId;

        const hasUnitPlan = Boolean(
          territoryPremiseStatsData?.basicInfo?.planId ||
          territoryPremiseStatsData?.planInfo?.id,
        );

        const patchPayload: any = {
          mapType: "TERRITORY_JSON",
          code: selectedUnitCode,
          remote_utility_id: getRemoteUtilityId(),
          entity_type: 11,
          utility_services: consumerUtilityServices,
        };

        if (!hasUnitPlan && consumerPlanId) {
          patchPayload.plan_id = Number(consumerPlanId);
        }

        await patchConfigMapMutation.mutateAsync(patchPayload);
      }
      setIsNoUtilityDialogOpen(false);
      await executeAddPremise(consumerUtilityServices);
    } catch (error: any) {
      console.error("Failed to attach utility services to unit:", error);
      toast({
        title: "Failed to attach utility services",
        description:
          error?.response?.data?.message ||
          error?.message ||
          "Could not attach consumer utility services to unit.",
        variant: "destructive",
      });
    } finally {
      setIsAttachingUtilityServices(false);
    }
  };

  // Execute the assigned service address update
  const executeAddPremise = async (servicesToAttach?: string[]) => {
    // Helper function to find premise data from the original API response
    const getPremiseData = (premiseId: string) => {
      return data?.result?.find((premise: any) => premise.code === premiseId);
    };

    // Helper function to extract hierarchy codes from premise data
    const getHierarchyCodes = (premiseData: any) => {
      const hierarchyCodes =
        premiseData?.hierarchy_codes || premiseData?.hierarchyCodes || {};
      return {
        city: hierarchyCodes.county || "",
        zone: hierarchyCodes.zone || "",
        division: hierarchyCodes.division || "",
        area: hierarchyCodes.area || "",
        sub_area: hierarchyCodes.subArea || hierarchyCodes.sub_area || "",
        premise: hierarchyCodes.premise || "",
      };
    };

    // Prepare payload with hierarchy codes
    const payload: any = {};

    const effectiveUtilityServices =
      servicesToAttach && servicesToAttach.length > 0
        ? servicesToAttach
        : unitUtilityServices;

    if (effectiveUtilityServices && effectiveUtilityServices.length > 0) {
      payload.utility_services = effectiveUtilityServices;
    }

    // Add service address data matching API payload requirement
    if (selectedServicePremiseIds.length > 0) {
      const servicePremiseData = getPremiseData(selectedServicePremiseIds[0]);
      const serviceHierarchy = getHierarchyCodes(servicePremiseData);
      const selectedUnit =
        selectedServiceUnitIds.length > 0
          ? selectedServiceUnitIds[0]
          : serviceAddressData.UNIT || "";

      payload.service_address_data = {
        unit: selectedUnit,
        address: serviceAddressData.ADDRESS || "",
        city: serviceHierarchy.city || "",
        zone: serviceHierarchy.zone || "",
        division: serviceHierarchy.division || "",
        area: serviceHierarchy.area || "",
        sub_area: serviceHierarchy.sub_area || "",
        premise: serviceHierarchy.premise || selectedServicePremiseIds[0] || "",
        zipcode: serviceAddressData.ZIPCODE || "",
        utility_services: effectiveUtilityServices,
      };

      payload.serviceAddressData = {
        unit: selectedUnit,
        address: serviceAddressData.ADDRESS || "",
        city: serviceHierarchy.city || "",
        zone: serviceHierarchy.zone || "",
        division: serviceHierarchy.division || "",
        area: serviceHierarchy.area || "",
        subArea: serviceHierarchy.sub_area || "",
        premise: serviceHierarchy.premise || selectedServicePremiseIds[0] || "",
        zipcode: serviceAddressData.ZIPCODE || "",
        utilityServices: effectiveUtilityServices,
      };

      try {
        const checkRes = await checkUnitWiseConsumerMutation.mutateAsync({
          unitCode: selectedUnit,
          remoteUtilityId: getRemoteUtilityId(),
          isCurrent: 1,
        });
        if (checkRes?.result && isOccupiedByAnotherConsumer(checkRes.result)) {
          setOccupiedConsumerInfo(checkRes.result);
          toast({
            title: "Unit already assigned",
            description: `This unit is already assigned to consumer ${
              checkRes.result.name || checkRes.result.consumerNo || ""
            }. Please select a different unit.`,
            variant: "destructive",
          });
          return;
        }
      } catch (error) {
        console.error("Failed to check unit wise consumer:", error);
      }
    }

    const consumerPlanId =
      consumerDetailsData?.result?.basicInfo?.planId ||
      consumerDetailsData?.result?.planId ||
      consumerDetailsData?.result?.remotePlanId ||
      consumerDetailsData?.result?.remote_plan_id ||
      consumerDetailsData?.result?.unitInfo?.basicInfo?.planId;

    const hasUnitPlan = Boolean(
      territoryPremiseStatsData?.basicInfo?.planId ||
      territoryPremiseStatsData?.planInfo?.id,
    );

    // If unit has no plan attached and consumer has a plan, attach plan to unit config map
    if (selectedUnitCode && !hasUnitPlan && consumerPlanId) {
      try {
        await patchConfigMapMutation.mutateAsync({
          mapType: "TERRITORY_JSON",
          code: selectedUnitCode,
          remote_utility_id: getRemoteUtilityId(),
          entity_type: 11,
          plan_id: Number(consumerPlanId),
        });
      } catch (err) {
        console.error("Failed to attach plan to unit:", err);
      }
    }

    const planId =
      consumerPlanId || territoryPremiseStatsData?.basicInfo?.planId;

    if (planId) {
      payload.remote_plan_id = Number(planId);
      payload.remotePlanId = Number(planId);
    }

    // Add ownership logic
    const structureChoice = ownershipDetails?.structureChoice;
    if (structureChoice === "Master") {
      payload.ownership = 0;
    } else if (structureChoice === "Sub_account") {
      payload.ownership = 1;
      payload.approval_details = {
        master_account_id:
          ownershipDetails?.approvalDetails?.masterAccountId ||
          ownershipDetails?.masterAccountId,
        master_account_no:
          ownershipDetails?.masterAccountNo ||
          ownershipDetails?.approvalDetails?.masterAccountNo,
        master_account_name:
          ownershipDetails?.masterAccountName ||
          ownershipDetails?.approvalDetails?.masterAccountName ||
          ownershipDetails?.masterAccountEmail,
        approved_at: ownershipDetails?.approvalDetails?.approvedAt,
      };
    } else if (
      structureChoice === "INDIVIDUAL" ||
      structureChoice === "Individual"
    ) {
      payload.ownership = 2;
    } else if (structureChoice === "Tenant") {
      payload.ownership = 4;
      const ownerDetail = ownershipDetails?.ownershipDetail || {};
      payload.approval_details = {
        owner_name: ownerDetail?.ownerName,
        owner_phone: ownerDetail?.ownerPhone,
        owner_email: ownerDetail?.ownerEmail,
        owner_type: ownerDetail?.ownerType,
        owner_address: ownerDetail?.ownerAddress,
      };
    }

    // Call the update assigned service address mutation
    updateAssignedServiceAddress(
      {
        consumerId,
        payload,
      },
      {
        onSuccess: () => {
          queryClient.refetchQueries({
            queryKey: ["cx", "consumerDetails"],
          });
          queryClient.refetchQueries({
            queryKey: ["cx", "consumerOnboarding"],
          });
          queryClient.refetchQueries({
            queryKey: ["onboarding", "territory-premise-stats"],
          });
          queryClient.invalidateQueries({
            predicate: (query) =>
              query.queryKey.some(
                (key) =>
                  typeof key === "string" &&
                  (key.includes("territoryPremiseStats") ||
                    key.includes("territory-premise-stats")),
              ),
          });
          setPremiseAdded(true);
          setDisablePassPremise(true);
        },
      },
    );
  };

  // Render location details for selected unit
  const renderUnitLocationDetails = () => {
    if (
      selectedServiceUnitIds.length === 0 ||
      !selectedUnitCode ||
      !territoryPremiseStatsData
    ) {
      return null;
    }

    const locationDetailItems = [
      {
        label: "Premise",
        value: (
          <span className="text-sm font-bold text-gray-900">
            {territoryPremiseStatsData?.parentInfo?.name || "N/A"}
          </span>
        ),
      },
      {
        label: "Unit",
        value: (
          <span className="text-sm font-bold text-gray-900">
            {territoryPremiseStatsData?.basicInfo?.name || "N/A"}
          </span>
        ),
      },
      {
        label: "Status",
        value: (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
              territoryPremiseStatsData?.basicInfo?.isActive
                ? "bg-emerald-100/70 text-emerald-600 border border-emerald-300"
                : "bg-red-100/70 text-red-600 border border-red-300"
            }`}
          >
            {territoryPremiseStatsData?.basicInfo?.isActive
              ? "Active"
              : "Inactive"}
          </span>
        ),
      },
      {
        label: "Parcel ID",
        value: (
          <span className="text-sm font-bold text-gray-900">
            {territoryPremiseStatsData?.parentInfo?.parcelId || "N/A"}
          </span>
        ),
      },
      {
        label: "Occupancy Status",
        value: (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100/70 text-emerald-600 border border-emerald-300">
            {territoryPremiseStatsData?.basicInfo?.occupancyStatus}
          </span>
        ),
      },
      {
        label: "Latitude",
        value: (
          <span className="text-sm font-bold text-blue-600">
            {territoryPremiseStatsData?.basicInfo?.latitude}
          </span>
        ),
      },
      {
        label: "Longitude",
        value: (
          <span className="text-sm font-bold text-blue-600">
            {territoryPremiseStatsData?.basicInfo?.longitude}
          </span>
        ),
      },
      {
        label: "Utility Services",
        value: (
          <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-blue-600">
            {unitUtilityServices.length > 0 ? (
              unitUtilityServices.map((service: string, idx: number) => (
                <span
                  key={idx}
                  className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100/80 text-blue-800 border border-blue-200"
                >
                  {service}
                </span>
              ))
            ) : (
              <span className="text-xs text-gray-400 font-normal">None</span>
            )}
          </div>
        ),
      },
    ];

    return (
      <div className="space-y-4 mt-4">
        {occupiedConsumerInfo &&
          isOccupiedByAnotherConsumer(occupiedConsumerInfo) && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h4 className="text-red-800 font-medium mb-3 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                This unit is already assigned to the following consumer:
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm text-red-900">
                <div>
                  <span className="font-semibold block text-red-950">
                    Consumer No
                  </span>
                  {occupiedConsumerInfo.consumerNo || "N/A"}
                </div>
                <div>
                  <span className="font-semibold block text-red-950">Name</span>
                  {occupiedConsumerInfo.name || "N/A"}
                </div>
                <div>
                  <span className="font-semibold block text-red-950">
                    Email
                  </span>
                  {occupiedConsumerInfo.email || "N/A"}
                </div>
              </div>
            </div>
          )}

        <div className="bg-blue-50/60 rounded-lg p-5 border border-blue-100">
          <h3 className="text-xs font-bold text-blue-900 mb-4 tracking-wide">
            Location Details
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-y-5 gap-x-6">
            {locationDetailItems.map((item, index) => (
              <ValueLabel key={index} label={item.label} value={item.value} />
            ))}
          </div>
        </div>
      </div>
    );
  };

  if (consumerDetailsData?.result?.paymentLastData?.isPlanPayment) {
    return null;
  }
  return (
    <div
      className={`bg-white rounded-lg border border-gray-200 shadow-sm ${className}`}
    >
      {/* Header */}
      <div
        className={`p-6 border-b border-gray-200 ${
          isCollapsible ? "cursor-pointer" : ""
        }`}
        onClick={toggleExpand}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3 flex-1">
            <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0 mt-1">
              <MapPin className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-gray-900">
                Select Premises
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Select service premise for this account (Max {maxSelection}{" "}
                each)
              </p>
            </div>
          </div>
          {isCollapsible && (
            <button
              type="button"
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors ml-2"
              aria-label={isExpanded ? "Collapse" : "Expand"}
            >
              {isExpanded ? (
                <ChevronUp className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500" />
              )}
            </button>
          )}
        </div>
      </div>

      {/* Form Content */}
      {isExpanded && (
        <div className="p-4">
          {/* Show message if no premises available */}
          {premiseOptions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No premises available
            </div>
          ) : (
            <>
              {/* Multi-Select Dropdowns using DynamicForm */}
              <DynamicForm
                fields={formFields}
                form={form}
                config={{
                  showSubmitButton: false,
                  showResetButton: false,
                  spacing: "large",
                }}
              />

              {/* Auto-Populated Location Details for Unit */}
              {renderUnitLocationDetails()}

              {/* Add Premise Button */}
              {selectedServicePremises.length > 0 && !isReadOnly && (
                <div className="flex justify-end mt-6">
                  {disablePassPremise ? (
                    <Button
                      type="button"
                      onClick={() => {
                        setDisablePassPremise(false);
                        setPremiseAdded(false);
                      }}
                      variant="outline"
                      disabled={isReadOnly}
                    >
                      Change Unit
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      onClick={handleAddPremise}
                      className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white"
                      disabled={isPending || isReadOnly}
                    >
                      Add Unit
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Dialog for Unit Without Utility Services */}
      <AttachUtilityServiceDialog
        isOpen={isNoUtilityDialogOpen}
        onClose={handleNoUtilityDecline}
        onConfirm={handleNoUtilityConfirm}
        unitName={territoryPremiseStatsData?.basicInfo?.name}
        consumerUtilityServices={consumerUtilityServices}
        isLoading={isAttachingUtilityServices}
      />
    </div>
  );
};

export default SelectServicePremise;
